import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { BetterAuthError } from "../utils";
import { mcpFetch } from "./mcpFetch";
import { applicationTypeFor } from "./schemas";
import { MCP_CLIENTS_QUERY_KEY } from "./useMcpClients";

export type UpdateMcpClientRedirectUrisInput = {
  clientId: string;
  redirectUris: string[];
};

/**
 * Points an existing credential at the platform's real callback URL, replacing the placeholder.
 * The application type goes with it, since the server checks it matches the new callbacks.
 *
 * Besides the redirect URIs, the endpoint only accepts the token endpoint auth method (see
 * `useUpdateMcpClientAuthMethod`). It refuses everything else, `client_name` included, because
 * renaming after registration would drop the company suffix the consent screen shows.
 */
export function useUpdateMcpClientRedirectUris(
  options?: UseMutationOptions<unknown, BetterAuthError, UpdateMcpClientRedirectUrisInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async ({ clientId, redirectUris }: UpdateMcpClientRedirectUrisInput) =>
      mcpFetch(
        "/oauth2/update-client",
        z.unknown(),
        {
          method: "POST",
          body: {
            client_id: clientId,
            update: {
              redirect_uris: redirectUris,
              application_type: applicationTypeFor(redirectUris),
            },
          },
        },
        "Could not save the callback URL",
      ),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: MCP_CLIENTS_QUERY_KEY,
        refetchType: "all",
      });
      onSuccess?.(...args);
    },
  });
}
